import { Component, ChangeDetectionStrategy, Input, EventEmitter, Output, OnInit } from '@angular/core';
import { BasketGiftOptionsDialogComponent } from '@features/basket-shared/basket-gift-options-dialog/basket-gift-options-dialog.component';
import {
    GiftOptionsDialogData,
    GiftOptionsDialogResponse
} from '@features/basket-shared/basket-gift-options-dialog/basket-gift-options-dialog.model';
import { filter, take } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OverlayService } from '@libs/overlay';
import { GiftWrappingCardViewModel, GiftWrappingViewModel } from '@models/api.models';
import { AddToBasketState } from '@features/product/product';
import { BehaviorSubject } from 'rxjs';
import { BasketService } from '@core/services/basket/basket.service';
import { ProductActionModel } from '@features/product/product-actions/product-actions';
import { GtmService } from '@core/services/gtm/gtm.service';
import { TranslateSafePipe } from '../../../@shared/translate-safe/translate-safe.pipe';
import { SafePipe } from '@shared/safe/safe.pipe';
import { LoadingDotsComponent } from '../../../@shared/loading-dots/loading-dots.component';
import { SvgIconComponent } from '../../../@shared/svg-icon/svg-icon.component';
import { NgClass, NgSwitch, NgSwitchCase, AsyncPipe } from '@angular/common';

@UntilDestroy()
@Component({
    selector: 'app-product-gift-options',
    templateUrl: './product-gift-options.component.html',
    styleUrls: ['./product-gift-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgSwitch, NgSwitchCase, SvgIconComponent, LoadingDotsComponent, AsyncPipe, SafePipe, TranslateSafePipe]
})
export class ProductGiftOptionsComponent implements OnInit {
    public state = new BehaviorSubject<AddToBasketState>('idle');

    @Input() public line: ProductActionModel;
    @Input() public amount = 1;
    @Input() public giftWrappingOptions?: GiftWrappingViewModel[];
    @Input() public giftCardOptions?: GiftWrappingCardViewModel[];
    @Input() public giftWrappingOptionsAllowed?: boolean;
    @Input() public giftCardText?: string;

    @Output() public giftOptionsChange: EventEmitter<GiftOptionsDialogResponse> = new EventEmitter();

    constructor(
        private overlayService: OverlayService,
        private basketService: BasketService,
        private gtmService: GtmService
    ) {}

    ngOnInit(): void {
        this.basketService
            .watchBasketNotifications(this.state)
            .pipe(untilDestroyed(this))
            .subscribe(_ => {
                this.state.next('idle');
            });
    }

    public giftOptionsDialog(): void {
        const ref = this.overlayService.open(BasketGiftOptionsDialogComponent, {
            data: {
                giftWrappingOptions: this.giftWrappingOptions || [],
                giftCardOptions: this.giftCardOptions || [],
                giftWrappingOptionsAllowed: this.giftWrappingOptionsAllowed,
                cardText: this.giftCardText,
                confirmButtonText: 'general.action.addToBasket'
            } as GiftOptionsDialogData
        });

        this.gtmService.trackSendAsAGiftClick(this.line);

        ref.afterClose()
            .pipe(take(1), untilDestroyed(this))
            .subscribe((response: GiftOptionsDialogResponse) => {
                if (!response) {
                    return;
                }

                this.addToBasket(response);
            });
    }

    private addToBasket(options: GiftOptionsDialogResponse): void {
        this.basketService.basketData$
            .pipe(
                take(1),
                filter(({ requestInProgress }) => !requestInProgress)
            )
            .subscribe(() => {
                this.state.next('loading');
                this.basketService.addToBasket(this.line.variantNo, this.amount, options);

                this.gtmService.trackSendAsAGiftToBasket(this.line);
            });
    }
}
